import { getRuntimeConfig } from '../config/runtime-config';

/**
 * Tenant branding (SDD-012, U-7').
 *
 * The legacy admin declares `GET /branding` → `BrandItem { name, logo, main_color, … }` but never
 * wired it: production serves a mocked settings service, and the colour every tenant sees is the
 * constant `#1dc4e9` (`Services/settings.ts`, `mock-data.ts`). So there is no endpoint to call,
 * and T-7 forbids inventing a fetch. The source is runtime config — per environment, like every
 * other address — with the frozen demo tenant as the default.
 *
 * The SHAPE is the legacy's `BrandItem`, in the console's casing, so that the day a branding
 * endpoint exists it replaces this function's body and nothing that renders a logo or an accent
 * changes.
 */
export interface Branding {
  /** The tenant's display name, when the environment provides one. */
  readonly name: string | null;
  /** The logo the brand block shows. The chrome falls back to the bundled EMIL logo if it fails to load. */
  readonly logo: string;
  /** `main_color` — the legacy's `--main-color`: the active nav item and its marks. */
  readonly mainColor: string;
  /** Where the values came from — so a screen can say "default" instead of pretending. */
  readonly source: 'config' | 'default';
}

/** The demo tenant as the legacy ships it (`legacy-chrome.tokens.json`, `mock-data.ts`). */
export const DEFAULT_MAIN_COLOR = '#1dc4e9';
/** The legacy's own served logo, relative to the suite's origin (`legacy-nav.json` `brand.logo`). */
export const DEFAULT_LOGO_PATH = '/assets/logos/emil_group.png';

export function brandingFromConfig(config: {
  readonly chrome: {
    readonly suiteUrl: string;
    readonly accent?: string;
    readonly logoUrl?: string;
    readonly tenantName?: string;
  };
}): Branding {
  const { accent, logoUrl, tenantName, suiteUrl } = config.chrome;
  const configured = accent !== undefined || logoUrl !== undefined;
  return {
    name: tenantName ?? null,
    logo: logoUrl ?? `${suiteUrl.replace(/\/+$/, '')}${DEFAULT_LOGO_PATH}`,
    mainColor: accent ?? DEFAULT_MAIN_COLOR,
    source: configured ? 'config' : 'default',
  };
}

/** Read at render time so Storybook and tests can inject a config after the bundle loaded. */
export function useBranding(): Branding {
  return brandingFromConfig(getRuntimeConfig());
}
